import { type ReactNode, useEffect, useState } from "react";

import { faCircleMinus, faCirclePlus, faSearch } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";
import { z } from "zod";

import type { LibraryListSettings } from "api/schemas/Library";
import type { Equipment, Movement, Position } from "api/services/content/schemas/shared";
import { useLibraryListSettings } from "routes/patients/PatientProfile/components/UserCourse/utils/useLibraryListSettings";
import { BaseButton } from "shared/atoms/BaseButton";
import { getActionButtonColorStyles } from "shared/atoms/BaseButton/BaseButton";
import { FilterDrawer, type FilterSection } from "shared/atoms/FilterDrawer";
import { MUISelect } from "shared/atoms/MUI";
import { Search } from "shared/molecules/Search";
import { type SortOption, sortAlphabetically } from "utils/array/sort";
import { useEquipmentOptions, useMovementOptions, usePositionOptions } from "utils/form";
import type { LibraryListSettingsStorageKey } from "utils/storage/storageConfig";
import { translateExerciseEquipment, translateMovement, translatePosition } from "utils/translations";
import { renderChipSelectValue } from "utils/ui/renderChipSelectValue";

import { IconLink } from "./IconLink";
import { RadioButtons } from "./RadioButtons";
import { TopBar, TopLeft, TopRight } from "./SharedStyledComponents";

export const TypeOptionSchema = z.enum(["all", "favourites", "added", "new"]);
export type TypeOption = z.infer<typeof TypeOptionSchema>;

interface LibraryHeaderProps {
  initialSelectedIds: number[];
  multiSelect?: boolean;
  numberOfSelectedItems: number;
  markAll: () => void;
  unmarkAll: () => void;
  jointOptions: { label: string; value: string }[];
  onSortChange: (sort: SortOption) => void;
  onSearchChange: (search: string) => void;
  onEquipmentChange?: (equipment: Equipment[]) => void;
  onMovementChange?: (movement: Movement[]) => void;
  onPositionChange?: (position: Position[]) => void;
  onPreviewJointChange: (joint: string) => void;
  onSelectedTypeOptionChange: (type: TypeOption) => void;
  libraryListSettingsStorageKey?: LibraryListSettingsStorageKey;
  pagination?: ReactNode;
}

export const LibraryHeader: React.FC<LibraryHeaderProps> = ({
  initialSelectedIds,
  multiSelect,
  numberOfSelectedItems,
  markAll,
  unmarkAll,
  jointOptions,
  onSortChange,
  onSearchChange,
  onEquipmentChange,
  onMovementChange,
  onPositionChange,
  onPreviewJointChange,
  onSelectedTypeOptionChange,
  libraryListSettingsStorageKey,
  pagination,
}) => {
  const {
    initialSearchTerm,
    searchTerm,
    setSearchTerm,
    equipment,
    setEquipment,
    movement,
    setMovement,
    position,
    setPosition,
    previewJoint,
    setPreviewJoint,
    selectedSort,
    setSelectedSort,
    selectedTypeOption,
    setSelectedTypeOption,
    resetLibraryListSettings,
    filterPristine,
  } = useLibraryListSettings(libraryListSettingsStorageKey);
  const { t } = useTranslation();
  const { register, setValue } = useForm<{ search: string }>();
  const theme = useTheme();
  const equipmentOptions = useEquipmentOptions();
  const movementOptions = useMovementOptions();
  const positionOptions = usePositionOptions();
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  useEffect(() => {
    onSortChange(selectedSort);
  }, [selectedSort, onSortChange]);

  useEffect(() => {
    onSearchChange(searchTerm);
  }, [searchTerm, setValue]);

  useEffect(() => {
    onPreviewJointChange(previewJoint);
  }, [previewJoint, onPreviewJointChange]);

  useEffect(() => {
    onSelectedTypeOptionChange(selectedTypeOption);
  }, [selectedTypeOption, onSelectedTypeOptionChange]);

  useEffect(() => {
    if (onEquipmentChange) {
      onEquipmentChange(equipment);
    }
  }, [equipment, onEquipmentChange]);

  useEffect(() => {
    if (onMovementChange) {
      onMovementChange(movement);
    }
  }, [movement, onMovementChange]);

  useEffect(() => {
    if (onPositionChange) {
      onPositionChange(position);
    }
  }, [position, onPositionChange]);

  const typeOptions: { label: string; value: TypeOption }[] = [
    { label: t("usercourse.add.all"), value: "all" },
    { label: t("usercourse.add.added"), value: "added" },
    { label: t("usercourse.add.new"), value: "new" },
  ];

  const sortOptions: { label: string; value: SortOption }[] = [
    { label: t("usercourse.add.a-z"), value: "a-z" },
    { label: t("usercourse.add.z-a"), value: "z-a" },
  ];

  const hasPreselection = initialSelectedIds.length > 0;

  const filterSections: FilterSection[] = [
    {
      title: t("form.preview_joint"),
      component: (
        <MUISelect
          value={previewJoint}
          options={jointOptions}
          onChange={e => setPreviewJoint(e.target.value as string)}
          size="small"
          displayEmpty
          sx={{ width: "100%" }}
        />
      ),
    },
  ];

  if (onPositionChange) {
    filterSections.push({
      title: t("library.exercises.fields.position"),
      component: (
        <MUISelect
          multiple
          value={position}
          options={sortAlphabetically({ list: positionOptions, selectedSort })}
          onChange={e => setPosition(e.target.value as LibraryListSettings["filter"]["position"])}
          renderValue={selected =>
            renderChipSelectValue({
              selected,
              translateFn: translatePosition,
              emptyMessage: t("usercourse.add.all_positions"),
            })
          }
          size="small"
          displayEmpty
          sx={{ width: "100%" }}
        />
      ),
    });
  }

  if (onMovementChange) {
    filterSections.push({
      title: t("library.exercises.fields.movement"),
      component: (
        <MUISelect
          multiple
          value={movement}
          options={movementOptions}
          onChange={e => setMovement(e.target.value as LibraryListSettings["filter"]["movement"])}
          renderValue={selected =>
            renderChipSelectValue({
              selected,
              translateFn: translateMovement,
              emptyMessage: t("usercourse.add.all_movements"),
            })
          }
          size="small"
          displayEmpty
          sx={{ width: "100%" }}
        />
      ),
    });
  }

  if (onEquipmentChange) {
    filterSections.push({
      title: t("library.exercises.fields.equipment"),
      component: (
        <MUISelect
          multiple
          value={equipment}
          options={sortAlphabetically({ list: equipmentOptions, selectedSort })}
          onChange={e => setEquipment(e.target.value as LibraryListSettings["filter"]["equipment"])}
          renderValue={selected =>
            renderChipSelectValue({
              selected,
              translateFn: translateExerciseEquipment,
              emptyMessage: t("usercourse.add.all_equipments"),
            })
          }
          size="small"
          displayEmpty
          sx={{ width: "100%" }}
        />
      ),
    });
  }

  return (
    <>
      {(multiSelect || hasPreselection) && (
        <TopBar>
          {multiSelect && (
            <StyledTopLeft>
              <div>{`${numberOfSelectedItems} ${t("usercourse.items_selected")}`}</div>
              <IconLink icon={faCirclePlus} title={t("usercourse.select_all")} onClick={markAll} />
              <IconLink icon={faCircleMinus} title={t("usercourse.deselect_all")} onClick={unmarkAll} />
            </StyledTopLeft>
          )}
          <StyledTopRight>
            {hasPreselection && (
              <RightAlign>
                <RadioButtons
                  value={selectedTypeOption}
                  options={typeOptions}
                  onChange={v => {
                    setSelectedTypeOption(v);
                  }}
                />
              </RightAlign>
            )}
          </StyledTopRight>
        </TopBar>
      )}
      <TopBar style={{ flexWrap: "wrap", gap: "16px" }}>
        <StyledTopLeft style={{ flexGrow: 0 }}>
          <Search
            icon={() => <FontAwesomeIcon icon={faSearch} size="sm" />}
            wrapperStyle={{ flexGrow: 1 }}
            inputStyle={{
              height: 40,
              fontSize: "14px",
              borderRadius: 8,
              border: `1px solid ${theme.colors.redesign.db20}`,
              minWidth: 300,
            }}
            {...register("search")}
            name="exerciseSearch"
            reset={() => {
              setValue("search", "");
              setSearchTerm("");
            }}
            placeholder={t("usercourse.search_exercise")}
            search={searchTerm}
            onChange={async e => {
              setSearchTerm(e.target.value);
            }}
            initialValue={initialSearchTerm}
          />

          <Column>
            <BaseButton
              variant="action"
              onClick={() => setFilterDrawerOpen(true)}
              text={t("form.filter")}
              icon="sliders-simple"
            />
          </Column>

          <Column>
            <StyledSelect
              value={selectedSort}
              onChange={e => setSelectedSort(e.target.value as SortOption)}
              size="medium"
              IconComponent={ExpandMoreIcon}
              displayEmpty
            >
              {sortOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label.toUpperCase()}
                </MenuItem>
              ))}
            </StyledSelect>
          </Column>

          {!filterPristine && (
            <BaseButton
              onClick={() => {
                resetLibraryListSettings();
                setValue("search", "");
              }}
              text={t("wiki.clear_filters")}
              variant="text"
              style={{ flexGrow: 0 }}
              small
            />
          )}
        </StyledTopLeft>
        <StyledTopRight style={{ flexGrow: 1 }}>{pagination}</StyledTopRight>
      </TopBar>

      <FilterDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        title={t("form.filter")}
        sections={filterSections}
        onClear={() => {
          resetLibraryListSettings();
          setValue("search", "");
        }}
      />
    </>
  );
};

const StyledSelect = styled(Select)`
  width: 100%;
  & .MuiOutlinedInput-notchedOutline {
    border: none;
  }
  & .MuiSelect-select {
    font-size: 14px !important;
    border-radius: 8px !important;
    padding: 12px 16px;
    line-height: 14px;
    box-sizing: border-box;
    width: 100%;
    font-weight: 500;

    ${getActionButtonColorStyles(true)}

    &:hover + input + svg {
      transition: 0.2s;
      fill: ${props => props.theme.colors.redesign.b100};
    }
  }
`;

const StyledTopLeft = styled(TopLeft)`
  gap: 8px;

  ${props => props.theme.belowBreakpoint} {
    flex-wrap: wrap;
  }
`;

const StyledTopRight = styled(TopRight)`
  gap: 8px;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
`;

const RightAlign = styled.div`
  justify-content: flex-end;
`;
